import { SDKBridge, SDKEnv, SDKStatusCode } from "@nebula-sdk/compose";
import { camelCase } from "es-toolkit";
import { NebulaSiteCore } from "./core";
import type { NebulaSiteBridgeEnv, NebulaSiteBridgeState } from "../type";
import type { SDKBridgeSHandler, SDKInitResult, SDKResult } from "@nebula-sdk/compose";
import type { App } from "vue";

export class NebulaSiteBridge {
  public constructor() {
    this.core = new NebulaSiteCore();
  }

  private readonly core: NebulaSiteCore;

  private readonly bridge = new SDKBridge();

  private readonly state: NebulaSiteBridgeState = {
    env: {
      scene: "",
      companyId: "",
      appId: "",
      channelId: "",
      openId: "",
      unionId: "",
      token: "",
    },
  };

  public get support(): boolean {
    return this.bridge.support;
  }

  public get env(): NebulaSiteBridgeEnv {
    return this.state.env;
  }

  public async track(args: {
    eventGroup?: string;
    eventLevel?: "info" | "warn" | "error";
    eventId: string;
    eventName: string;
    eventValue: Record<string, unknown>;
  }): Promise<SDKResult<null>> {
    const navigator = window.navigator as Navigator & {
      connection?: { effectiveType?: string };
      userAgentData?: { platform?: string };
    };

    return await this.core.track({
      appId: this.core.options?.config.nbAppId ?? "",
      appVersion: this.core.version,
      appChannel: SDKEnv.Web,
      userId: this.core.userInfo?.nbOpenId ?? "",
      userTag: this.core.userInfo?.openId ?? "",
      deviceId: this.core.deviceId,
      deviceModel: navigator.userAgentData?.platform ?? "unknown",
      osType: this.core.platform,
      osVersion: navigator.userAgent,
      networkType: navigator.connection?.effectiveType ?? "unknown",
      eventId: args.eventId,
      eventName: args.eventName,
      eventGroup: args.eventGroup ?? "",
      eventValue: args.eventValue,
      eventTime: this.core.formatDateTime(Date.now()),
      eventLevel: args.eventLevel ?? "info",
    });
  }

  public async invoke<T = any, R = any>(
    handler: string,
    args: T,
    options: { json: boolean },
  ): Promise<R> {
    if (!this.bridge.support) return null as R;

    const result = await this.bridge.invoke<T, R>(handler, args, options);

    void this.track({
      eventId: handler,
      eventName: "桥接调用",
      eventGroup: "site-bridge-invoke",
      eventValue: { args, result },
      eventLevel: "info",
    });

    return result;
  }

  public subscribe(
    handler: string,
    callback: SDKBridgeSHandler,
    options: { async: boolean },
  ): void {
    if (!this.bridge.support) return;

    this.bridge.subscribe(handler, callback, options);

    void this.track({
      eventId: handler,
      eventName: "桥接订阅",
      eventGroup: "site-bridge-subscribe",
      eventValue: { args: { handler, options }, result: null },
      eventLevel: "info",
    });
  }

  public unsubscribe(
    handler: string,
    callback: SDKBridgeSHandler,
    options: { async: boolean },
  ): void {
    if (!this.bridge.support) return;

    this.bridge.unsubscribe(handler, callback, options);

    void this.track({
      eventId: handler,
      eventName: "桥接取消订阅",
      eventGroup: "site-bridge-unsubscribe",
      eventValue: { args: { handler, options }, result: null },
      eventLevel: "info",
    });
  }

  public async init(): Promise<SDKInitResult> {
    if (!this.support) {
      return {
        status: "error",
        code: SDKStatusCode.Error,
        msg: "不在APP中无法初始化",
        data: null,
      };
    }

    const options = await this.invoke(
      "FYPlatformJSInterface.asynHandler",
      { action: "getParams", data: {} },
      { json: true },
    );

    for (const key in options) {
      this.state.env[camelCase(key)] = options[key];
    }

    const result = await this.core.init({
      version: import.meta.env.VITE_SDK_VERSION,
      debug: false,
      config: {
        appId: this.state.env.appId,
        appKey: "",
        passportCode: "feiyu3",
        paymentCode: "feiyu3",
        nbAppId: this.state.env.appId,
        nbAppKey: "",
        nbCompanyId: this.state.env.companyId,
        nbChannelId: this.state.env.channelId,
        nbChannelTemplateId: "",
        nbChannelTemplateCode: "feiyu3",
      },
    });

    if (result.status === "success") {
      this.core.userInfo = {
        openId: this.state.env.openId,
        unionId: this.state.env.unionId,
        token: this.state.env.token,
        scene: "",
        nbOpenId: this.state.env.openId,
        nbUnionId: this.state.env.unionId,
        firstLogin: false,
        firstRegisterTime: 0,
      };
    }

    return result;
  }

  public install(app: App): void {
    Object.defineProperty(app.config.globalProperties, "$bridge", {
      enumerable: true,
      get: () => this,
    });
  }
}

export default new NebulaSiteBridge();
